import { atom, useRecoilState } from "recoil";

import RestApiCall, { isRequestCancelled, generateCancelToken } from "interceptor/aeh";
import Environment from "services/environment";
import { requirePartyId } from "utils/partyIdUtils";
import { API_FETCH_STATUS, PARTY_AGGREGATION_LEVEL } from "constants/common";

const env = Environment.getEnv();

const { LOADING, ERROR, SUCCESS } = API_FETCH_STATUS;

const ALL_ACCOUNTS = "All Accounts";

let cancelToken;

const fetchPartiesAPI = ({ payload, headers, cancelToken }) => {
  const apiPayload = {
    method: "POST",
    url: "user/profile/v3/fetch/root/accounts",
    baseURL: env.profileBaseUrl,
    config: { ivUser: env.ivUser },
    data: payload,
    cancelToken,
    headers: { ...headers },
  };
  return RestApiCall.api(apiPayload);
};

const INITIAL_STATE = {
  state: null,
  partyId: null,
  partyDetails: {
    partyId: null,
    partyName: ALL_ACCOUNTS,
  },
  defaultPartyAccount: null,
  singlePartyAccount: false,
};

const partyAccountsAtom = atom({
  key: "partyAccounts",
  default: { ...INITIAL_STATE },
});

const usePartyAccounts = () => {
  const [partyAccountsState, setPartyAccountsState] = useRecoilState(partyAccountsAtom);

  const { partyId, partyDetails, defaultPartyAccount, singlePartyAccount } = partyAccountsState;
  const updateStateProperties = (updatedProperties) => {
    setPartyAccountsState((prevState) => ({ ...prevState, ...updatedProperties }));
  };

  const setPartyAccount = (partyDetails) => {
    updateStateProperties({ partyDetails, partyId: partyDetails?.partyId });
  };

  const fetchSelectedParty = ({ lobDetails, partyId: partyIdFromUrl, product: productFromUrl, validatedPartyRef }) => {
    if (lobDetails) {
      const { key: lob } = lobDetails;
      if (productFromUrl && !partyIdFromUrl && !requirePartyId(lob)) {
        updateStateProperties({
          partyDetails: {
            partyId: null,
            partyName: `All ${productFromUrl} Accounts`,
          },
          partyId: null,
          defaultPartyAccount: null,
        });
        return;
      }

      if (requirePartyId(lob)) {
        if (!partyIdFromUrl) {
          updateStateProperties({
            partyDetails: {
              partyId: null,
              partyName: `All ${lobDetails.displayName} Accounts`,
            },
            partyId: null,
            defaultPartyAccount: null,
          });
          return;
        }

        fetchPartyAccounts({
          rowCount: { key: 1 },
          pageNo: 0,
          lob,
          partyAggregationLevel: null,
          partyIdFromUrl,
        })
          .then((res) => {
            if (res.data?.data) {
              let partyDetails = null;

              const { userPartyData, defaultPartyAccount, partyUniqueCount, viewMode } = res.data.data;

              const { partyAccounts } = userPartyData[0];

              if (
                partyAccounts &&
                partyAccounts.length &&
                partyAccounts[0].partyId === partyIdFromUrl &&
                ((partyUniqueCount === 1 && viewMode === "DEFAULT") || (partyUniqueCount === 0 && viewMode === "ACCORDION"))
              ) {
                partyDetails = partyAccounts[0];
              } else {
                partyDetails = {
                  partyId: null,
                  partyName: `All ${productFromUrl ?? lobDetails.displayName} Accounts`,
                };
              }

              updateStateProperties({
                partyDetails,
                partyId: partyDetails.partyId,
                defaultPartyAccount,
              });
              validatedPartyRef.current = true;
              return partyDetails;
            } else if (res.data.code && res.data.message) {
              return res.data;
            } else {
              throw new Error(res);
            }
          })
          .catch((err) => {
            return err;
          });
      } else {
        updateStateProperties({
          partyDetails: {
            partyId: null,
            partyName: `All ${lobDetails.displayName} Accounts`,
          },
          partyId: null,
          defaultPartyAccount: null,
        });
        return;
      }
    } else {
      updateStateProperties({
        partyDetails: {
          partyId: null,
          partyName: ALL_ACCOUNTS,
        },
        partyId: null,
        defaultPartyAccount: null,
      });
    }
  };

  const fetchPartyAccounts = ({
    partyIdFromUrl,
    lob,
    emailId,
    partyAggregationLevel,
    rowCount,
    pageNo,
    searchValue,
    searchType,
    product,
  }) => {
    updateStateProperties({ state: LOADING });
    const headers = {};
    const payload = {
      request: {
        emailId,
        partyAggregationLevel: partyIdFromUrl ? null : partyAggregationLevel ? partyAggregationLevel : "EMAIL",
      },

      paginationRequired: true,
      pagination: {
        limit: rowCount ? rowCount.key : 10,
        offSet: pageNo === 0 ? 0 : (pageNo - 1) * (rowCount ? rowCount.key : 10),
      },
      searchQueries: [],
    };

    if (partyAggregationLevel !== PARTY_AGGREGATION_LEVEL.EMAIL) {
      payload.request.lob = lob;
      headers.lob = lob;
    }
    if (partyIdFromUrl) {
      payload.searchQueries.push({
        columnLabel: "partyId",
        columnValue: partyIdFromUrl,
      });
    }
    if (partyAggregationLevel === PARTY_AGGREGATION_LEVEL.PRODUCT) {
      payload.searchQueries.push({
        columnLabel: "matchProductName",
        columnValue: product,
      });
    }

    if (searchValue?.length > 2) {
      payload.searchQueries.push({ columnLabel: searchType.key, columnValue: searchValue });
    }

    if (cancelToken) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    cancelToken = generateCancelToken();
    return new Promise((resolve, reject) => {
      fetchPartiesAPI({ payload, headers, cancelToken: cancelToken?.token })
        .then((res) => {
          if (res.data?.data) {
            updateStateProperties({ state: SUCCESS });
            resolve(res);
            return res;
          } else if (res.data.code && res.data.message) {
            reject(res);
          } else {
            throw new Error(res);
          }
        })
        .catch((err) => {
          if (!isRequestCancelled(err)) {
            updateStateProperties({ state: ERROR });
            reject(err);
          }
        });
    });
  };

  const fetchSingleParty = (lob) => {
    fetchPartyAccounts({ lob, rowCount: { key: 1 }, pageNo: 0 })
      .then((res) => {
        if (res.data?.data) {
          let partyDetails = null;
          const { userPartyData, viewMode } = res.data.data;
          const { totalCount, partyAccounts } = userPartyData[0];
          if (totalCount === 1 && viewMode != "ACCORDIAN") {
            partyDetails = partyAccounts[0];
            updateStateProperties({
              partyDetails,
              partyId: partyDetails.partyId,
              singlePartyAccount: true,
            });
          } else {
            updateStateProperties({
              singlePartyAccount: false,
              partyId: null,
            });
          }
        } else {
          throw new Error(res);
        }
      })
      .catch((err) => {
        if (!isRequestCancelled(err)) {
          updateStateProperties({ state: ERROR });
        }
      });
  };

  return {
    partyId,
    partyDetails,
    defaultPartyAccount,
    setPartyAccount,
    fetchSelectedParty,
    fetchPartyAccounts,
    fetchSingleParty,
    singlePartyAccount,
  };
};

export default usePartyAccounts;
